import { useMemo } from 'react';
import { costFormater, useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { api } from '@/api';
import { SHORT_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';

import { PlanDetailRowData } from '../EmployerPlanDetails.types';
import { getFundingMethodLabel } from '../EmployerPlanDetails.utils';

const formatDate = (date?: Date) => (date ? dayjs(date).format(SHORT_DATE_FORMAT) : '');

const useGetHsaPlanDetailsQuery = (planId: string) => {
  const { isLoading, data, isError, refetch, isSuccess } = useQuery(
    [API_URL.ACCOUNT_USAGE_PLANS_PLAN_DETAILS_HSA_BY_PLAN_ID(''), planId],
    () => api.get(API_URL.ACCOUNT_USAGE_PLANS_PLAN_DETAILS_HSA_BY_PLAN_ID(planId)),
    {
      retry: 0,
      enabled: !!planId,
    }
  );

  const planDetails = useMemo(() => data?.data, [data?.data]);

  const formattedData = useMemo<PlanDetailRowData[]>(() => {
    if (!planDetails) {
      return [];
    }
    const rows: PlanDetailRowData[] = [
      {
        key: 'planYear',
        label: 'Plan year',
        value: `${formatDate(planDetails.plan_year_valid_from)} - ${
          planDetails.plan_year_valid_to ? formatDate(planDetails.plan_year_valid_to) : 'Ongoing'
        }`,
      },
      ...(planDetails.investment_threshold
        ? [
            {
              key: 'investmentThreshold',
              label: 'Investment threshold',
              value: costFormater(planDetails.investment_threshold),
            },
          ]
        : []),
      ...(planDetails.cip_closure_days
        ? [
            {
              key: 'cipClosureDays',
              label: 'CIP closure days',
              value: `${planDetails.cip_closure_days}`,
            },
          ]
        : []),
      ...(planDetails.funding_method
        ? [
            {
              key: 'fundingMethod',
              label: 'Funding method',
              value: getFundingMethodLabel(planDetails.funding_method) || '',
            },
          ]
        : []),
      ...(planDetails.pend_contributions_for_cip
        ? [
            {
              key: 'pendContributionsForCip',
              label: 'Pend contributions for CIP',
              value: planDetails.pend_contributions_for_cip ? 'Yes' : 'No',
            },
          ]
        : []),
    ];

    return rows;
  }, [planDetails]);

  return {
    isLoading,
    isError,
    isSuccess,
    refetch,

    data: formattedData,
  };
};

export default useGetHsaPlanDetailsQuery;
